import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface AsyncInputProps<T = string>
  extends Omit<React.ComponentProps<'input'>, 'onChange' | 'value'> {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  loadSuggestions?: (query: string) => Promise<T[]>;
  debounceMs?: number;
  minChars?: number;
  maxSuggestions?: number;
  renderSuggestion?: (
    suggestion: T,
    index: number,
    isHighlighted: boolean,
    closeSuggestions: () => void,
  ) => React.ReactNode;
  renderNoResults?: () => React.ReactNode;
  renderStaticLabel?: () => React.ReactNode;
  error?: string | boolean;
  required?: boolean;
}

const AsyncInput = <T = string,>(
  props: AsyncInputProps<T> & { ref?: React.Ref<HTMLInputElement> },
) => {
  const {
    value = '',
    onChange,
    placeholder = '',
    className = '',
    disabled = false,
    loadSuggestions,
    debounceMs = 300,
    minChars = 1,
    maxSuggestions = 30,
    renderSuggestion,
    renderNoResults,
    renderStaticLabel,
    error,
    required = false,
    ref,
    ...inputProps
  } = props;
  const [suggestions, setSuggestions] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(
    null,
  );
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, []);

  const debouncedLoad = useCallback(
    async (query: string) => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      const timer = setTimeout(async () => {
        if (!loadSuggestions || query.length < minChars) {
          setSuggestions([]);
          setIsLoading(false);
          return;
        }

        setIsLoading(true);
        try {
          const results = await loadSuggestions(query);
          setSuggestions(results.slice(0, maxSuggestions));
          setShowSuggestions(true);
        } catch (error) {
          console.error('Error loading suggestions:', error);
          setSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      }, debounceMs);

      setDebounceTimer(timer);
    },
    [loadSuggestions, debounceMs, minChars, maxSuggestions],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      onChange(newValue);

      if (newValue !== value) {
        setHighlightedIndex(-1);
        if (newValue.trim()) {
          updateDropdownPosition();
          setShowSuggestions(true);
          debouncedLoad(newValue);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      }
    },
    [onChange, value, debouncedLoad, updateDropdownPosition],
  );

  const closeSuggestions = useCallback(() => {
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  }, []);

  const handleSuggestionClick = useCallback(
    (suggestion: T) => {
      onChange(String(suggestion));
      setSuggestions([]);
      closeSuggestions();
      inputRef.current?.focus();
    },
    [onChange, closeSuggestions],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showSuggestions || suggestions.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : prev,
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
            handleSuggestionClick(suggestions[highlightedIndex]);
          }
          break;
        case 'Escape':
          closeSuggestions();
          break;
      }
    },
    [
      showSuggestions,
      suggestions,
      highlightedIndex,
      handleSuggestionClick,
      closeSuggestions,
    ],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (suggestionsRef.current?.contains(e.relatedTarget as Node)) {
        return;
      }
      closeSuggestions();
    },
    [closeSuggestions],
  );

  const handleFocus = useCallback(() => {
    updateDropdownPosition();
    if (value && value.length >= minChars) {
      setShowSuggestions(true);
      if (suggestions.length === 0) {
        debouncedLoad(value);
      }
    }
  }, [
    value,
    minChars,
    suggestions.length,
    debouncedLoad,
    updateDropdownPosition,
  ]);

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  // Update position when suggestions are shown or on scroll/resize
  useEffect(() => {
    if (showSuggestions) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);

      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [showSuggestions, updateDropdownPosition]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        closeSuggestions();
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions, closeSuggestions]);

  const hasVisibleSuggestions =
    showSuggestions && (suggestions.length > 0 || isLoading);

  const isInvalid = Boolean(error) || (required && !value);
  const errorMessage = typeof error === 'string' ? error : '';
  const displayError =
    errorMessage || (isInvalid && required ? 'Required' : '');

  const renderDropdown = () => {
    // Show dropdown if we have suggestions to display or if we're loading
    if (!showSuggestions || !dropdownPosition) return null;
    if (typeof document === 'undefined') return null;

    // Don't render if we have no content to show
    const hasContent = isLoading || suggestions.length > 0;
    if (!hasContent) return null;

    return createPortal(
      <div
        ref={suggestionsRef}
        style={{
          position: 'absolute',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
          zIndex: 9999,
        }}
        className="tw-bg-white tw-text-foreground tw-border tw-border-border tw-rounded-md tw-shadow-md tw-max-h-60 tw-overflow-y-auto"
      >
        {isLoading && (
          <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-muted-foreground tw-flex tw-items-center tw-gap-2">
            <div className="tw-w-4 tw-h-4 tw-border-2 tw-border-border tw-border-t-primary tw-rounded-full tw-animate-spin" />
            Loading...
          </div>
        )}

        {!isLoading &&
          suggestions.length === 0 &&
          (renderNoResults ? (
            renderNoResults()
          ) : (
            <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-muted-foreground">
              No suggestions found
            </div>
          ))}

        {!isLoading &&
          suggestions.length > 0 &&
          renderStaticLabel &&
          renderStaticLabel()}

        {!isLoading &&
          suggestions.map((suggestion, index) => {
            const isHighlighted = index === highlightedIndex;

            if (renderSuggestion) {
              return (
                <div
                  key={index}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {renderSuggestion(
                    suggestion,
                    index,
                    isHighlighted,
                    closeSuggestions,
                  )}
                </div>
              );
            }

            return (
              <button
                key={index}
                type="button"
                className={cn(
                  'tw-w-full tw-text-left tw-px-3 tw-py-2 tw-text-sm tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors',
                  'hover:tw-bg-accent hover:tw-text-accent-foreground focus:tw-bg-accent focus:tw-text-accent-foreground focus:tw-outline-none',
                  isHighlighted && 'tw-bg-accent tw-text-accent-foreground',
                )}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {String(suggestion)}
              </button>
            );
          })}
      </div>,
      document.body,
    );
  };

  return (
    <div ref={containerRef} className="tw-relative tw-w-full">
      <Input
        {...inputProps}
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={cn(
          'tw-w-full',
          'tw-bg-white',
          'tw-placeholder:text-muted-foreground',
          className,
        )}
        disabled={disabled}
        autoComplete="off"
        aria-invalid={isInvalid}
        error={error}
        required={required}
      />
      {renderDropdown()}
    </div>
  );
};

AsyncInput.displayName = 'AsyncInput';

export default AsyncInput;
